import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DatePicker }           from '@mui/x-date-pickers'
import { AdapterMoment }        from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers'
import moment                   from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { shallowEqual } from 'react-redux'
import {
  Alert, Box, Button, Checkbox, ClickAwayListener, Divider, FormControl,
  InputAdornment, MenuItem, Paper, Popper, Snackbar,
  Select, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material'
import SearchIcon      from '@mui/icons-material/Search'
import SettingsIcon    from '@mui/icons-material/Settings'
import VisibilityIcon    from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import EventIcon         from '@mui/icons-material/Event'
import {
  selectBeacon4Filters,
  selectBeacon4PerfTech,
  selectBeacon4PerfMainKpi,
  selectBeacon4PerfFiltersRemoved,
  selectBeacon4LegendVisible,
  selectBeacon4ActivitiesVisible,
  selectBeacon4PerfActivityTeam,
  selectBeacon4ActivityTeamOptions,
  selectBeacon4PerfActivityType,
  selectBeacon4ActivityTypeOptions,
  selectBeacon4PerfActivities,
  selectBeacon4ActivityOptions,
  selectBeacon4PerfActivitiesLoading,
  selectBeacon4PerfDataAggregation,
  setBeacon4AdminDialog,
  setBeacon4Aggregation,
  setBeacon4PerfSide, setBeacon4PerfPolygon, setBeacon4PerfVendor, setBeacon4PerfJvid,
  setBeacon4PerfDataAggregation,
  clearBeacon4PerfFiltersRemoved,
  toggleBeacon4Legend,
  toggleBeacon4Activities, setBeacon4PerfActivityTeam, setBeacon4PerfActivityType, setBeacon4PerfActivities,
  setBeacon4DateFrom, setBeacon4DateTo,
  setBeacon4PerfTech, setBeacon4PerfMainKpi,
  loadBeacon4PerformanceNetworkOverviewFilters,
  loadBeacon4PerformanceNetworkOverviewActivities,
} from '../../reducers/Beacon4FiltersReducer'
import { reloadBeacon4PerformanceNetworkOverviewView } from '../../actions/Beacon4PerformanceNetworkOverviewModuleActions'
import {
  selectBeacon4PerformanceNetworkOverviewAvailableTechs,
  selectBeacon4PerformanceNetworkOverviewAvailableKpisByTech,
} from '../../reducers/Beacon4PerformanceNetworkOverviewKpiDataReducer'

const AGG_OPTIONS = ['Daily', 'Weekly', 'Monthly']

// "Data Aggregation" options — value tokens match what the backend's
// DataAggregation field expects (Beacon4PerformanceNetworkOverviewKpiDataRepository.Build
// / Beacon4PerformanceNetworkOverviewKpiDataService.BuildSeriesAsync), "KPI" shown
// to the user but stored as "Kpi".
const DATA_AGGREGATION_OPTIONS = [
  { value: 'Side',    label: 'Side' },
  { value: 'Polygon', label: 'Polygon' },
  { value: 'Vendor',  label: 'Vendor' },
  { value: 'Kpi',     label: 'KPI' },
]

function Label({ children }) {
  return (
    <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', mb: '3px' }}>
      {children}
    </Typography>
  )
}

// ── MultiSelectDropdown ──────────────────────────────────────────────────────

// Virtualised list — only renders visible rows (fixes performance with many JV IDs)
const ITEM_H = 28
const MAX_VISIBLE = 8

const VirtualList = memo(function VirtualList({ items, selectedSet, onToggle }) {
  // items: [{ value, label }]
  const [scrollTop, setScrollTop] = useState(0)
  const containerH = Math.min(items.length, MAX_VISIBLE) * ITEM_H

  if (items.length === 0) {
    return <MenuItem disabled sx={{ fontSize: 11 }}>No results</MenuItem>
  }

  const startIdx   = Math.floor(scrollTop / ITEM_H)
  const endIdx     = Math.min(startIdx + MAX_VISIBLE + 1, items.length)
  const visible    = items.slice(startIdx, endIdx)
  const paddingTop = startIdx * ITEM_H

  return (
    <Box
      onScroll={e => setScrollTop(e.currentTarget.scrollTop)}
      sx={{ height: containerH, overflowY: 'auto', position: 'relative', py: 0.5 }}
    >
      <Box sx={{ height: items.length * ITEM_H, position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: paddingTop, left: 0, right: 0 }}>
          {visible.map(item => (
            <Box key={item.value} onClick={() => onToggle(item.value)}
              sx={{ height: ITEM_H, display: 'flex', alignItems: 'center', px: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
              <Checkbox checked={selectedSet.has(item.value)} size="small" sx={{ p: 0.5 }} disableRipple />
              <Typography noWrap sx={{ fontSize: 11 }}>{item.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
})

// `options` accepts either plain strings (Side/Polygon/Vendor/JV ID — value
// and display label are the same) or { value, label } objects, for cases
// where the identity (e.g. an Activity's numeric id) differs from what's
// shown/searched (e.g. "Feature · Team (start → end)"). `value`/onChange
// still deal only in comma-joined VALUES, same as before.
const MultiSelectDropdown = memo(function MultiSelectDropdown({ label, value = '', options = [], onChange, disabled }) {
  const [open, setOpen]     = useState(false)
  const [search, setSearch] = useState('')
  const anchorRef           = useRef(null)
  const onChangeRef         = useRef(onChange)
  useEffect(() => { onChangeRef.current = onChange }, [onChange])

  const normalized = useMemo(
    () => options.map(o => (o && typeof o === 'object') ? o : { value: o, label: o }),
    [options]
  )

  const selectedSet = useMemo(() => new Set(typeof value === 'string' ? value.split(',').filter(Boolean) : value), [value])
  const filtered    = useMemo(
    () => normalized.filter(o => o.label.toLowerCase().includes(search.toLowerCase())),
    [normalized, search]
  )

  const toggle          = useCallback((val) => {
    const next = new Set(selectedSet)
    next.has(val) ? next.delete(val) : next.add(val)
    onChangeRef.current([...next].join(','))
  }, [selectedSet])

  const handleSelectAll = useCallback(() => { onChangeRef.current(filtered.map(o => o.value).join(',')) }, [filtered])
  const handleClear     = useCallback(() => { onChangeRef.current('') }, [])
  const handleClose     = useCallback(() => { setOpen(false); setSearch('') }, [])
  const selected        = useMemo(() => [...selectedSet], [selectedSet])
  const selectedLabels  = useMemo(
    () => selected.map(val => normalized.find(o => o.value === val)?.label ?? val),
    [selected, normalized]
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Omitted entirely (not just blank) when no label is passed — lets a
          FieldGroup with a single field (e.g. "Data Aggregation") rely on
          its own title instead of repeating it a second time underneath. */}
      {label && <Label>{label}</Label>}
      <Box ref={anchorRef}>
        <Button
          variant="outlined" size="small" disabled={disabled}
          onClick={() => !disabled && setOpen(v => !v)}
          sx={{
            height: 28, fontSize: 11, minWidth: 130, maxWidth: 200,
            textTransform: 'none', justifyContent: 'space-between',
            color: selected.length ? 'text.primary' : 'text.disabled',
            borderColor: 'divider', bgcolor: 'background.default',
            '&:hover': { borderColor: 'primary.main' },
            overflow: 'hidden',
          }}
          endIcon={<Typography sx={{ fontSize: 10, ml: 0.5 }}>▾</Typography>}
        >
          <Typography noWrap sx={{ fontSize: 11, maxWidth: 150 }}>
            {selected.length === 0 ? '(All)' : selected.length === 1 ? selectedLabels[0] : `${selected.length} selected`}
          </Typography>
        </Button>
      </Box>

      <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" sx={{ zIndex: 1400 }}>
        <ClickAwayListener onClickAway={handleClose}>
          <Paper elevation={4} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden', minWidth: 200, maxWidth: 260 }}>
            <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <TextField
                size="small" fullWidth autoFocus placeholder="Search..."
                value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 14, color: 'text.disabled' }} /></InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { height: 26, fontSize: 11 } }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Button size="small" sx={{ fontSize: 10, textTransform: 'none', py: 0 }} onClick={handleClear}>Clear</Button>
              <Button size="small" sx={{ fontSize: 10, textTransform: 'none', py: 0 }} onClick={handleSelectAll}>Select all</Button>
            </Box>
            <VirtualList items={filtered} selectedSet={selectedSet} onToggle={toggle} />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  )
})

// ── FilterSelect ─────────────────────────────────────────────────────────────
function FilterSelect({ label, value, options = [], onChange, disabled }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {label && <Label>{label}</Label>}
      <FormControl size="small" disabled={disabled}>
        <Select
          value={value ?? ''} onChange={e => onChange(e.target.value)} displayEmpty
          sx={{ height: 28, fontSize: 11, minWidth: 110, bgcolor: 'background.default', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' } }}
        >
          <MenuItem value="" sx={{ fontSize: 11 }}>(All)</MenuItem>
          {options.map(o => <MenuItem key={o} value={o} sx={{ fontSize: 11 }}>{o}</MenuItem>)}
        </Select>
      </FormControl>
    </Box>
  )
}

// ── FilterBar ────────────────────────────────────────────────────────────────
// Visual group wrapper — adds a subtle "title" above a horizontal row of fields
function FieldGroup({ title, children }) {
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: 0.25,
      border: '1px solid', borderColor: 'divider',
      borderRadius: 1, px: 1, py: 0.5,
      bgcolor: 'rgba(0,0,0,0.015)',
    }}>
      <Typography sx={{
        fontSize: 8.5, fontWeight: 700, color: 'text.disabled',
        textTransform: 'uppercase', letterSpacing: '0.08em',
        lineHeight: 1, mb: -0.25, whiteSpace: 'nowrap',
      }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        {children}
      </Box>
    </Box>
  )
}


export default function Beacon4FilterBar({ exportButton, externalRole }) {
  const dispatch      = useDispatch()
  const { aggregation, dateFrom, dateTo, perfSide, perfPolygon, perfVendor, perfJvid, perfSides, perfPolygons, perfVendors, perfJvids } =
    useSelector(selectBeacon4Filters, shallowEqual)
  const perfFiltersRemoved = useSelector(selectBeacon4PerfFiltersRemoved)
  const legendVisible = useSelector(selectBeacon4LegendVisible)
  const activitiesVisible     = useSelector(selectBeacon4ActivitiesVisible)
  const perfActivityTeam      = useSelector(selectBeacon4PerfActivityTeam)
  const activityTeamOptions   = useSelector(selectBeacon4ActivityTeamOptions)
  const perfActivityType      = useSelector(selectBeacon4PerfActivityType)
  const activityTypeOptions   = useSelector(selectBeacon4ActivityTypeOptions)
  const perfActivities        = useSelector(selectBeacon4PerfActivities)
  const activityOptionsRaw    = useSelector(selectBeacon4ActivityOptions)
  const perfActivitiesLoading = useSelector(selectBeacon4PerfActivitiesLoading)
  const perfDataAggregation   = useSelector(selectBeacon4PerfDataAggregation)
  const perfTech      = useSelector(selectBeacon4PerfTech)
  const perfMainKpi   = useSelector(selectBeacon4PerfMainKpi)
  const canAdmin      = externalRole === 'Administrator' || externalRole === 'Manager'
  const perfTechs        = useSelector(selectBeacon4PerformanceNetworkOverviewAvailableTechs)
  const perfKpisByTech   = useSelector(selectBeacon4PerformanceNetworkOverviewAvailableKpisByTech)
  const canApply = !!(dateFrom && dateTo && dateFrom <= dateTo) && !!perfMainKpi

  // All KPI keys (Main + Breakdown) offered for a given Tech — used to decide
  // whether a Tech change should keep the current Main/Table KPI (and by
  // extension the filter selections under them) or reset them, instead of
  // always resetting outright.
  const kpiKeysForTech = useCallback(
    (t) => (perfKpisByTech?.[t] ?? []).map(k => k.key),
    [perfKpisByTech]
  )

  // Auto-correct perfTech if it isn't in the available techs (e.g. DB only has 3G+).
  // Fires whenever the available list changes.
  useEffect(() => {
    if (!perfTechs?.length) return
    if (!perfTechs.includes(perfTech)) {
      const nextTech = perfTechs[0]
      dispatch(setBeacon4PerfTech({ tech: nextTech, availableKeys: kpiKeysForTech(nextTech) }))
    }
  }, [dispatch, perfTechs, perfTech, kpiKeysForTech])

  // KPI Selection is the first point of contact: once both Tech and Main KPI
  // are chosen, fetch the Side/Polygon/Vendor/JV ID options scoped to that
  // KPI's source view and the current date range. Re-fires on Tech/KPI
  // change (may resolve to a different view → reducer resets the current
  // selections) and on date range change too (Last_Date is filtered by that
  // range — same view usually → reducer keeps the current selections).
  useEffect(() => {
    if (!perfTech || !perfMainKpi) return
    if (!dateFrom || !dateTo) return
    dispatch(loadBeacon4PerformanceNetworkOverviewFilters({ tech: perfTech, mainKpiKey: perfMainKpi, dateFrom, dateTo }))
  }, [dispatch, perfTech, perfMainKpi, dateFrom, dateTo])

  // Activities are strictly scoped to the active dateFrom/dateTo window (see
  // GetActivityMarkers on the backend — full containment, not overlap), AND
  // to whatever Side/Vendor the user already picked in the "Filters" group —
  // showing an activity outside those filters wouldn't make sense on a chart
  // that's itself scoped to them. Refetches live whenever any of these
  // change while the panel is open — same behaviour as the Side/Polygon/
  // Vendor/JV ID options above (loadBeacon4PerformanceNetworkOverviewFilters),
  // which also refresh as soon as Tech/KPI/dates are picked, without waiting
  // for Apply. Fetching only while `activitiesVisible` avoids hitting a
  // second module's API for users who never touch this feature.
  useEffect(() => {
    if (!activitiesVisible) return
    if (!dateFrom || !dateTo) return
    dispatch(loadBeacon4PerformanceNetworkOverviewActivities({ dateFrom, dateTo, side: perfSide, vendor: perfVendor }))
  }, [dispatch, activitiesVisible, dateFrom, dateTo, perfSide, perfVendor])

  // Options for the "Activity" multiselect — id as value (what actually gets
  // stored/compared), descriptive text as label. Already scoped to Tech +
  // the selected Type(s) by selectBeacon4ActivityOptions, so Type isn't
  // repeated in the label here (it has its own dropdown right next to it).
  const activityOptions = useMemo(
    () => (activityOptionsRaw ?? []).map(a => ({
      value: String(a.id),
      label: `${a.title || a.feature || `Activity ${a.id}`} · ${a.teamName ?? 'No team'} (${a.startDate} → ${a.endDate})`,
    })),
    [activityOptionsRaw]
  )

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Paper elevation={0} square sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, px: 2.5, pt: 1, pb: 0.75 }}>

          {/* ── ROW 1: Date · Options ───────────────────── */}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap' }}>

            {/* GROUP: Date range */}
            <FieldGroup title="Date">
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Label>First Day</Label>
                <DatePicker
                  format="YYYY-MM-DD"
                  value={dateFrom ? moment(dateFrom, 'YYYY-MM-DD') : null}
                  onChange={v => dispatch(setBeacon4DateFrom(v?.isValid() ? v.format('YYYY-MM-DD') : ''))}
                  maxDate={dateTo ? moment(dateTo, 'YYYY-MM-DD').subtract(1, 'day') : undefined}
                  slotProps={{ textField: { size: 'small', sx: { width: 138, '& .MuiOutlinedInput-root': { height: 28, fontSize: 11 } } } }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Label>Last Day</Label>
                <DatePicker
                  format="YYYY-MM-DD"
                  value={dateTo ? moment(dateTo, 'YYYY-MM-DD') : null}
                  onChange={v => dispatch(setBeacon4DateTo(v?.isValid() ? v.format('YYYY-MM-DD') : ''))}
                  minDate={dateFrom ? moment(dateFrom, 'YYYY-MM-DD').add(1, 'day') : undefined}
                  slotProps={{ textField: { size: 'small', sx: { width: 138, '& .MuiOutlinedInput-root': { height: 28, fontSize: 11 } } } }}
                />
              </Box>
            </FieldGroup>

            {/* GROUP: KPI Selection — right after Date. Determines the Tech
                that both the dashboard AND the Activities cascade (Row 2)
                are scoped to, so it comes before everything downstream of it. */}
            <FieldGroup title="KPI Selection">
              {/* Tech */}
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Label>Tech</Label>
                <ToggleButtonGroup exclusive value={perfTech} size="small"
                  onChange={(_, v) => { if (v) dispatch(setBeacon4PerfTech({ tech: v, availableKeys: kpiKeysForTech(v) })) }}
                  sx={{ height: 28 }}>
                  {(perfTechs ?? []).map(t => (
                    <ToggleButton key={t} value={t} sx={{
                      px: 1.25, fontSize: 11, fontWeight: 500, textTransform: 'none',
                      '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } },
                    }}>{t}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
              {/* KPI */}
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Label>KPI *</Label>
                <FormControl size="small">
                  <Select
                    value={perfMainKpi || ''}
                    onChange={e => dispatch(setBeacon4PerfMainKpi(e.target.value))}
                    displayEmpty
                    sx={{ height: 28, fontSize: 11, minWidth: 220, bgcolor: 'background.default',
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' } }}>
                    <MenuItem value="" sx={{ fontSize: 11, color: 'text.disabled' }}>(Select KPI)</MenuItem>
                    {/* Backend returns ALL active KPIs (Main + Breakdowns).
                        This dropdown drives the dashboard, so it must show
                        only Main KPIs — filter by category here. */}
                    {(perfKpisByTech?.[perfTech] ?? [])
                      .filter(k => k.category === 'Main KPI')
                      .map(k => (
                        <MenuItem key={k.key} value={k.key} sx={{ fontSize: 11 }}>{k.label}</MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>
            </FieldGroup>

            {/* GROUP: Options — Legend, Export, Manage KPIs (admin only). */}
            <FieldGroup title="Options">
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Label>&nbsp;</Label>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Tooltip title={legendVisible ? 'Hide legend' : 'Show legend'}>
                    <ToggleButton value="legend" selected={legendVisible} size="small"
                      onChange={() => dispatch(toggleBeacon4Legend())}
                      sx={{
                        height: 28, px: 1.25, fontSize: 11, textTransform: 'none', gap: 0.5,
                        '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } },
                      }}>
                      {legendVisible ? <VisibilityIcon sx={{ fontSize: 15 }} /> : <VisibilityOffIcon sx={{ fontSize: 15 }} />}
                      Legend
                    </ToggleButton>
                  </Tooltip>
                  <Tooltip title={activitiesVisible ? 'Hide activities' : 'Show activities'}>
                    <ToggleButton value="activities" selected={activitiesVisible} size="small"
                      onChange={() => dispatch(toggleBeacon4Activities())}
                      sx={{
                        height: 28, px: 1.25, fontSize: 11, textTransform: 'none', gap: 0.5,
                        '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } },
                      }}>
                      <EventIcon sx={{ fontSize: 15 }} />
                      Activities
                    </ToggleButton>
                  </Tooltip>
                  {exportButton}
                  {canAdmin && (
                    <Tooltip title="Manage KPIs — definitions, formulas, breakdowns and audit">
                      <Button variant="outlined" size="small" startIcon={<SettingsIcon sx={{ fontSize: 16 }} />}
                        onClick={() => dispatch(setBeacon4AdminDialog(true))}
                        sx={{ height: 28, px: 1.5, fontSize: 11, textTransform: 'none', whiteSpace: 'nowrap' }}>
                        Manage KPIs
                      </Button>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </FieldGroup>

            <Box sx={{ flex: 1 }} />
          </Box>

          {/* ── ROW 2: Filters + Activities + Time + Apply ──────────────────── */}
          <Box sx={{
            display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap',
            pt: 0.75, borderTop: '1px dashed', borderColor: 'divider',
          }}>
            {/* GROUP: Filters — Side/Polygon/Vendor/JV ID, scoped to the KPI
                just picked above (its source view) and the current date
                range. Sits right after KPI Selection since it literally
                can't resolve to anything before a KPI is chosen —
                disabled/empty until then. */}
            <FieldGroup title="Filters">
              <MultiSelectDropdown label="Side" value={perfSide} options={perfSides}
                onChange={v => dispatch(setBeacon4PerfSide(v))} disabled={!perfMainKpi} />
              <MultiSelectDropdown label="Polygon" value={perfPolygon} options={perfPolygons}
                onChange={v => dispatch(setBeacon4PerfPolygon(v))} disabled={!perfMainKpi || perfPolygons.length === 0} />
              <MultiSelectDropdown label="Vendor" value={perfVendor} options={perfVendors}
                onChange={v => dispatch(setBeacon4PerfVendor(v))} disabled={!perfMainKpi || perfVendors.length === 0} />
              <MultiSelectDropdown label="JV ID" value={perfJvid} options={perfJvids}
                onChange={v => dispatch(setBeacon4PerfJvid(v))} disabled={!perfMainKpi} />
            </FieldGroup>

            {/* GROUP: Activities — injected/shown when "Activities" is
                toggled on in Options (Row 1). Each Activity Tracker entry
                carries its own Tech, so this Team > Type > Activity cascade
                is scoped to the Tech picked in KPI Selection (Row 1), same
                way Filters above is scoped to the KPI picked there. Team,
                Type and Activity are three separate dropdowns (not one
                combined field) — picking a Team narrows Type, which narrows
                Activity, mirroring the Side > Polygon > Vendor > JV ID
                cascade in Filters. */}
            {activitiesVisible && (
              <FieldGroup title="Activities">
                <MultiSelectDropdown
                  label="Team"
                  value={perfActivityTeam}
                  options={activityTeamOptions}
                  onChange={v => dispatch(setBeacon4PerfActivityTeam(v))}
                  disabled={perfActivitiesLoading || activityTeamOptions.length === 0}
                />
                <MultiSelectDropdown
                  label="Type"
                  value={perfActivityType}
                  options={activityTypeOptions}
                  onChange={v => dispatch(setBeacon4PerfActivityType(v))}
                  disabled={perfActivitiesLoading || activityTypeOptions.length === 0}
                />
                <MultiSelectDropdown
                  label="Activity"
                  value={perfActivities}
                  options={activityOptions}
                  onChange={v => dispatch(setBeacon4PerfActivities(v))}
                  disabled={perfActivitiesLoading || activityOptions.length === 0}
                />
                {perfActivitiesLoading && (
                  <Typography sx={{ fontSize: 10, color: 'text.disabled', alignSelf: 'center' }}>Loading…</Typography>
                )}
                {!perfActivitiesLoading && activityOptions.length === 0 && (
                  <Typography sx={{ fontSize: 10, color: 'text.disabled', alignSelf: 'center', maxWidth: 100, lineHeight: 1.3 }}>
                    No activities for this Tech/date range
                  </Typography>
                )}
              </FieldGroup>
            )}

            {/* GROUP: Data (Aggregation) — which dimensions the CHART's own
                series break out by (separate from the Time aggregation
                dropdown next to it, and from perfAggregationMode which only
                drives the breakdown table). Defaults to Side+Vendor, i.e.
                the previously-hardcoded behaviour — see
                Beacon4FiltersReducer's perfDataAggregation initial state.
                "KPI" is NOT selected by default and is force-included
                server-side whenever a panel ends up with more than one KPI
                (Main + Breakdown/Breakdown2) regardless of this checkbox —
                checking/unchecking it here only has an effect when a panel
                has just one. */}
            <FieldGroup title="Data">
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Label>Aggregation</Label>
                <FormControl size="small">
                  <MultiSelectDropdown
                    value={perfDataAggregation}
                    options={DATA_AGGREGATION_OPTIONS}
                    onChange={v => dispatch(setBeacon4PerfDataAggregation(v))}
                  />
                </FormControl>
              </Box>
            </FieldGroup>

            {/* GROUP: Time Aggregation — right next to the KPI picker it
                applies to. A dropdown rather than a toggle group to keep
                this row narrow / wrapping less. (Not FilterSelect — that
                always adds an "(All)" option, which doesn't make sense
                here: aggregation always has a definite value, never "none".) */}
            <FieldGroup title="Time">
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Label>Aggregation</Label>
                <FormControl size="small">
                  <Select
                    value={aggregation}
                    onChange={e => dispatch(setBeacon4Aggregation(e.target.value))}
                    sx={{ height: 28, fontSize: 11, minWidth: 100, bgcolor: 'background.default',
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' } }}>
                    {AGG_OPTIONS.map(a => <MenuItem key={a} value={a} sx={{ fontSize: 11 }}>{a}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            </FieldGroup>

            {/* Spacer pushes Apply to the true right edge of this row. Apply
                only fires once a KPI is chosen. */}
            <Box sx={{ flex: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button variant="contained" size="small"
                onClick={() => dispatch(reloadBeacon4PerformanceNetworkOverviewView())}
                disabled={!canApply}
                sx={{ height: 30, px: 3, fontWeight: 600 }}>
                Apply
              </Button>
            </Box>
          </Box>

        </Box>
      </Paper>

      {/* Notice for filters dropped on the last refetch (KPI/date range
          resolved to a source view where a previously-picked Side/Polygon/
          Vendor/JV ID no longer exists). Anything that's still valid is kept
          silently — this only fires for the part that genuinely couldn't be
          carried over, so it doesn't nag the user on every single refetch. */}
      <Snackbar
        open={perfFiltersRemoved.length > 0}
        autoHideDuration={8000}
        onClose={() => dispatch(clearBeacon4PerfFiltersRemoved())}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity="info" variant="filled"
          onClose={() => dispatch(clearBeacon4PerfFiltersRemoved())}
          sx={{ fontSize: 12 }}
        >
          {perfFiltersRemoved.map(r => `${r.field}: ${r.values.join(', ')}`).join(' · ')} — not available for this KPI/date range, removed.
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  )
}
